import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Container,
  CircularProgress,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  useMediaQuery,
  Badge,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Typography,
  TextField,
  InputAdornment,
  // useNavigate, // Removed duplicate import
  // Link // Removed duplicate import from MUI
  keyframes // Added for CSS animation
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useNavigate, Link } from 'react-router-dom'; // Keep this one
import activityTracker from '../../utils/activityTracker'; // Import activity tracker

// Icons
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SearchIcon from '@mui/icons-material/Search';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'; // Wishlist Icon
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CategoryIcon from '@mui/icons-material/Category';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import CommentIcon from '@mui/icons-material/Comment';
import ArticleIcon from '@mui/icons-material/Article';
import Collections from '@mui/icons-material/Collections';
import Tune from '@mui/icons-material/Tune';
import DescriptionIcon from '@mui/icons-material/Description'; // Icon for Content Pages
import LogoutIcon from '@mui/icons-material/Logout';
import CampaignIcon from '@mui/icons-material/Campaign';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'; // Import Calendar icon
import AssessmentIcon from '@mui/icons-material/Assessment'; // Import Reports icon
import MailOutlineIcon from '@mui/icons-material/MailOutline'; // Import Email Templates icon
import NotificationsIcon from '@mui/icons-material/Notifications'; // Import Push Notifications icon
import ShoppingBagOutlined from '@mui/icons-material/ShoppingBagOutlined';

// Context and Services
import { AuthContext } from '../../context/AuthContext';
import { useMenu } from '../../context/MenuContext';
import { getCartCount } from '../../services/cartService';
import { useWishlist } from '../../context/WishlistContext'; // Import Wishlist context hook
import { getAllConfigs } from '../../services/configService'; // Added to fetch banner texts
import { parseMultiLangContent } from '../../utils/imageHelper'; // Re-added for menu titles
import { COLORS, FONTS } from '../../theme/tokens';
import useNavOverflow from './useNavOverflow';
import LanguageSwitcher from './LanguageSwitcher';
import useI18nConfig from '../../hooks/useI18nConfig';
import { getActiveLanguage } from '../../i18n';
import { useTranslation } from 'react-i18next';

const GIFT_CARDS_ITEM = {
  id: 'gift-cards',
  type: 'gift-card',
  title: 'Gift Cards',
  link: '/product/gift-card',
};

const menuItemPath = (item) => {
  if (item.type === 'attribute' || item.type === 'blog' || item.type === 'gift-card') {
    return item.link;
  }

  return `/category/${item.slug}`;
};

// Styled Components (can be refined further)
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper, // Use paper background (white by default)
  color: theme.palette.text.primary,
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)', // Subtle shadow
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.appBar,
}));

const Logo = styled('img')(({ theme }) => ({
  height: '50px',
  verticalAlign: 'middle',
  [theme.breakpoints.down('md')]: {
    height: '32px',
    width: '100%',
    maxWidth: '100%',
    objectFit: 'contain',
  },
}));

const LogoLink = styled(Link)({
  display: 'inline-flex',
  alignItems: 'center',
  textDecoration: 'none',
});

const IconsContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  minWidth: 0,
});

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: COLORS.textPrimary,
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  '&:hover': {
    backgroundColor: 'rgba(58,79,65,0.06)',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '22px',
  },
  [theme.breakpoints.down('md')]: {
    width: '36px',
    height: '36px',
    padding: theme.spacing(0.75),
    '& .MuiSvgIcon-root': {
      fontSize: '20px',
    },
  },
}));

const SearchContainer = styled(Box)({
  color: COLORS.textSecondary,
  minWidth: 0,
});

// Simple fade animation for the banner
const fadeInOut = keyframes`
  0%, 100% { opacity: 0; }
  10%, 90% { opacity: 1; }
`;

const BannerContainer = styled(Box)(({ theme }) => ({
  backgroundColor: COLORS.green,
  color: COLORS.footerText,
  minHeight: '34px',
  padding: theme.spacing(0.5, 2),
  textAlign: 'center',
  fontFamily: FONTS.body,
  fontSize: '0.8125rem',
  letterSpacing: '0.3px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.6875rem',
    padding: theme.spacing(0.5, 1),
  },
}));

const BannerText = styled(Typography)({
  display: 'inline-block',
  width: '100%',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  letterSpacing: 'inherit',
  animation: `${fadeInOut} 5s ease-in-out infinite`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: 1,
  },
});

const UnifiedHeader = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cartItemsCount, setCartItemsCount] = useState(0);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [searchQuery, setSearchQuery] = useState(''); // State for search input
  const [mobileSearchVisible, setMobileSearchVisible] = useState(false); // State for mobile search visibility
  const [bannerTexts, setBannerTexts] = useState([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Use 'md' breakpoint for mobile menu trigger
  const { user, isAuthenticated, logout } = useContext(AuthContext);
  const { menuCategories, loading, error, fetchMenuCategories } = useMenu();
  const { itemCount: wishlistCount } = useWishlist(); // Get wishlist item count
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const activeLang = getActiveLanguage();
  // `loaded` matters: until the config answers, the switcher must stay hidden
  // rather than render from the defaults.
  const { config: i18nConfig, loaded: i18nLoaded } = useI18nConfig();
  const navItems = useMemo(
    () => [
      ...(menuCategories || []).filter(
        (item) => (
          (item.type === 'category' || item.type === 'attribute')
          && parseMultiLangContent(item.title, activeLang).trim().length > 0
        )
      ),
      { ...GIFT_CARDS_ITEM, title: t('header.giftCards') },
    ],
    [menuCategories, activeLang, i18n.language, t]
  );
  const { containerRef, visibleItems, overflowItems } = useNavOverflow(navItems);

  const isAdmin = user && user.is_admin >= 1;

  // Fetch menu categories, cart count, and banner texts
  useEffect(() => {
    fetchMenuCategories();
    fetchCartItems();
    fetchBannerTexts(); // Fetch banner texts on mount

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchCartItems();
    };
    const handleCartUpdate = () => fetchCartItems();
    const handleCartReset = (event) => {
      if (event.detail && event.detail.forceZero) {
        setCartItemsCount(0);
        // Also clear localStorage here to ensure consistency
        localStorage.removeItem('cart_items');
        localStorage.removeItem('cart_count');
      } else {
        fetchCartItems();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('cartReset', handleCartReset);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('cartReset', handleCartReset);
    };
  }, [fetchMenuCategories]);

  const fetchCartItems = async () => {
    if (document.visibilityState === 'hidden') return;
    try {
      const count = await getCartCount();
      setCartItemsCount(count);
    } catch (error) {
      console.error('[UnifiedHeader] Error fetching cart count:', error); // Log error
      setCartItemsCount(0);
    }
  };

  // Fetch banner texts from config
  const fetchBannerTexts = async () => {
    try {
      const response = await getAllConfigs();
      if (response.success && response.data && Array.isArray(response.data.bannerTexts)) {
        const configuredTexts = response.data.bannerTexts.filter((text) => text && text.trim());
        setBannerTexts(configuredTexts);
      } else {
        setBannerTexts([]);
      }
    } catch (error) {
      console.error("Error fetching banner texts:", error);
      setBannerTexts([]);
    }
  };

  // Effect for banner carousel
  useEffect(() => {
    if (bannerTexts.length > 1) {
      const intervalId = setInterval(() => {
        setCurrentBannerIndex((prevIndex) => (prevIndex + 1) % bannerTexts.length);
      }, 5000); // Change text every 5 seconds (matches animation duration)

      return () => clearInterval(intervalId); // Cleanup interval on unmount
    }
  }, [bannerTexts]);

  // Effect to listen for external search clear events
  useEffect(() => {
    const handleClearSearch = () => {
      // console.log('[UnifiedHeader] Received clearSearch event.'); // Optional: for debugging
      setSearchQuery('');
    };

    window.addEventListener('clearSearch', handleClearSearch);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener('clearSearch', handleClearSearch);
    };
  }, []); // Empty dependency array ensures this runs only once on mount


  // Menu item title parsing (active storefront language)
  const getTitle = (titleJson) => {
    return parseMultiLangContent(titleJson, activeLang);
  };

  const navItemColor = (item) => {
    const title = getTitle(item.title);
    if (/^sale$/i.test(title)) return COLORS.wine;
    if (item.type === 'gift-card') return COLORS.goldText;
    return COLORS.textPrimary;
  };

  const desktopNavItemSx = (item) => ({
    appearance: 'none',
    bgcolor: 'transparent',
    border: 0,
    borderBottom: '2px solid transparent',
    color: navItemColor(item),
    cursor: 'pointer',
    fontFamily: FONTS.body,
    fontSize: { md: '0.6875rem', lg: '0.8125rem' },
    fontWeight: 600,
    letterSpacing: { md: '1px', lg: '1.5px' },
    lineHeight: 1.5,
    py: '6px',
    px: 0,
    textDecoration: 'none',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    '&:hover': {
      borderBottomColor: COLORS.gold,
      color: COLORS.green,
    },
  });

  const drawerNavItemSx = (item) => ({
    color: navItemColor(item),
    fontFamily: FONTS.body,
    fontSize: '0.8125rem',
    fontWeight: 600,
    letterSpacing: '1.5px',
    lineHeight: 1.5,
    textDecoration: 'none',
    textTransform: 'uppercase',
    '&:hover': { color: COLORS.green },
  });

  const openMoreMenu = (event) => setMoreMenuAnchor(event.currentTarget);
  const closeMoreMenu = () => setMoreMenuAnchor(null);
  const closeDrawerNavigation = () => {
    setSearchQuery('');
    setDrawerOpen(false);
  };

  const handleIconClick = (path) => {
    setSearchQuery(''); // Clear search on icon click navigation
    navigate(path); // Use navigate
    if (isMobile) setDrawerOpen(false);
  };

  const toggleDrawer = () => setDrawerOpen(!drawerOpen);

  // Profile Menu Handlers
  const handleProfileMenuOpen = (event) => setProfileMenuAnchor(event.currentTarget);
  const handleProfileMenuClose = () => setProfileMenuAnchor(null);
  const handleProfileMenuItemClick = (path) => {
    setSearchQuery(''); // Clear search on profile menu navigation
    navigate(path);
    handleProfileMenuClose();
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
    navigate('/');
  };

  // Function to perform the actual search navigation
  const performSearch = (query) => {
    if (query.trim() !== '') {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);

      // Track search activity
      activityTracker.trackSearch(query.trim());

      // Optionally clear search query after navigation or hide mobile input
      // setSearchQuery('');
      // setMobileSearchVisible(false);
    }
  };

  // Search Submit Handler — fires on Enter key or search icon click
  const handleSearchSubmit = (event) => {
    if (event.key === 'Enter' && searchQuery.trim().length >= 1) {
      event.preventDefault();
      performSearch(searchQuery);
    }
  };
  
  // Toggle Mobile Search Input Visibility
  const toggleMobileSearch = () => setMobileSearchVisible(!mobileSearchVisible);

  // Admin menu items definition (same as before)
  const adminMenuItems = [
    { text: 'Admin Dashboard', icon: <DashboardIcon fontSize="small" />, path: '/admin' },
    { text: 'Products', icon: <InventoryIcon fontSize="small" />, path: '/admin/products' },
    { text: 'Orders', icon: <ReceiptIcon fontSize="small" />, path: '/admin/orders' },
    { text: 'Coupons & Gift Cards', icon: <LocalOfferIcon fontSize="small" />, path: '/admin/coupons' },
    { text: 'Categories', icon: <CategoryIcon fontSize="small" />, path: '/admin/categories' },
    { text: 'Attributes', icon: <CategoryIcon fontSize="small" />, path: '/admin/attributes' },
    { text: 'Variations', icon: <Tune fontSize="small" />, path: '/admin/variations' },
    { text: 'Gallery', icon: <Collections fontSize="small" />, path: '/admin/gallery' },
    { text: 'Customers', icon: <PeopleIcon fontSize="small" />, path: '/admin/customers' },
    { text: 'Customer Messaging', icon: <CampaignIcon fontSize="small" />, path: '/admin/messaging' },
    { text: 'Reviews', icon: <CommentIcon fontSize="small" />, path: '/admin/reviews' },
    { text: 'Blog Posts', icon: <ArticleIcon fontSize="small" />, path: '/admin/blog' },
    { text: 'Content Pages', icon: <DescriptionIcon fontSize="small" />, path: '/admin/pages' },
    // { text: 'Email Templates', icon: <MailOutlineIcon fontSize="small" />, path: '/admin/email-templates' }, // REMOVED - Using file templates now
    { text: 'Settings', icon: <SettingsIcon fontSize="small" />, path: '/admin/settings' },
    { text: 'Calendar', icon: <CalendarMonthIcon fontSize="small" />, path: '/admin/calendar' }, // Added Calendar link
    { text: 'Reports', icon: <AssessmentIcon fontSize="small" />, path: '/admin/reports' }, // Added Reports link
    { text: 'Push Notifications', icon: <NotificationsIcon fontSize="small" />, path: '/admin/push-notifications' } // Added Push Notifications link
  ];

  // Render Loading/Error states for menu
  if (loading) {
    return (
      <StyledAppBar position="sticky">
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: 'center', minHeight: '64px' }}>
            <CircularProgress size={24} color="inherit" />
          </Toolbar>
        </Container>
      </StyledAppBar>
    );
  }
  if (error) {
    return (
      <StyledAppBar position="sticky">
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: 'center', minHeight: '64px' }}>
            <Typography color="error">{error}</Typography>
          </Toolbar>
        </Container>
      </StyledAppBar>
    );
  }

  // Main Header Render
  return (
    <>
      {/* Banner Section */}
      {bannerTexts && bannerTexts.length > 0 && (
        <BannerContainer data-testid="announcement-bar">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              alignItems: 'center',
              width: '100%',
              maxWidth: '1232px',
              mx: 'auto',
              px: { xs: '8px', sm: '16px', md: '24px' },
              gap: 1,
            }}
          >
            <BannerText variant="body2">
              {typeof bannerTexts[currentBannerIndex] === 'object'
                ? parseMultiLangContent(bannerTexts[currentBannerIndex], activeLang)
                : bannerTexts[currentBannerIndex]}
            </BannerText>
            {!isMobile && (
              <LanguageSwitcher
                enabled={i18nLoaded && i18nConfig.switcherEnabled}
                languagesInSwitcher={i18nConfig.languagesInSwitcher}
                variant="banner"
              />
            )}
          </Box>
        </BannerContainer>
      )}
      {/* Main AppBar */}
      <StyledAppBar position="sticky">
        <Container maxWidth={false} disableGutters>
          <Toolbar
            disableGutters
            data-testid="header-logo-row"
            sx={{
              display: 'grid',
              gridTemplateColumns: isMobile
                ? 'auto minmax(0, 1fr) auto'
                : '1fr auto 1fr',
              alignItems: 'center',
              minHeight: { xs: '56px', md: '74px' },
              maxWidth: '1232px',
              mx: 'auto',
              px: { xs: '8px', sm: '16px', md: '24px' },
            }}
          >
            {/* Left: desktop search; mobile menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              {isMobile && (
                <IconButton
                  color="inherit"
                  aria-label="menu"
                  onClick={toggleDrawer}
                  sx={{ width: 36, height: 36, p: 0.75 }}
                >
                  <MenuIcon sx={{ fontSize: 24 }} />
                </IconButton>
              )}
              {!isMobile && (
                <SearchContainer>
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder={t('header.searchPlaceholder')}
                    inputProps={{ 'aria-label': 'Search products' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: 20,
                        color: COLORS.textSecondary,
                        '& .MuiInputAdornment-root': { color: COLORS.textSecondary },
                        '& input::placeholder': { color: COLORS.textSecondary, opacity: 1 },
                      }
                    }}
                    sx={{ width: 200 }}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleSearchSubmit}
                  />
                </SearchContainer>
              )}
            </Box>

            {/* Center: logo */}
            <LogoLink
              to="/"
              onClick={() => setSearchQuery('')}
              sx={{
                justifySelf: 'center',
                justifyContent: 'center',
                minWidth: 0,
                width: { xs: '100%', md: 'auto' },
              }}
            >
              <Logo src="/eladorelogo-2.png" alt="Eladore Ruffles" />
            </LogoLink>

            {/* Right: language, mobile search, wishlist, account, and bag */}
            <IconsContainer>
               {/* Show switcher in toolbar when no banner, or always on mobile (banner is desktop-only for switcher) */}
               {(isMobile || !bannerTexts || bannerTexts.length === 0) && (
                 <LanguageSwitcher
                   enabled={i18nLoaded && i18nConfig.switcherEnabled}
                   languagesInSwitcher={i18nConfig.languagesInSwitcher}
                   variant="header"
                 />
               )}
               {isMobile && !mobileSearchVisible && (
                 <StyledIconButton aria-label="search" onClick={toggleMobileSearch}>
                   <SearchIcon />
                 </StyledIconButton>
               )}

               {/* Wishlist Icon Placeholder */}
               <Tooltip title={t('nav.wishlist')}>
                 <StyledIconButton aria-label="wishlist" onClick={() => { setSearchQuery(''); navigate('/wishlist'); }}> {/* Clear search and navigate */}
                   <Badge badgeContent={wishlistCount} color="secondary"> {/* Use secondary color for wishlist badge */}
                     <FavoriteBorderIcon />
                   </Badge>
                 </StyledIconButton>
              </Tooltip>

              {/* User Account Icon/Menu */}
              <Tooltip title={isAuthenticated ? t('nav.myAccount') : t('nav.loginRegisterShort')}>
                <StyledIconButton
                  onClick={isAuthenticated ? handleProfileMenuOpen : () => handleIconClick('/login')}
                  aria-label={isAuthenticated ? t('nav.myAccount') : t('nav.loginRegisterShort')}
                  aria-controls={Boolean(profileMenuAnchor) ? 'profile-menu-appbar' : undefined}
                  aria-haspopup="true"
                >
                  {isAuthenticated && user ? (
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: 'primary.main', // Use theme color
                        fontSize: '1rem'
                      }}
                    >
                      {user.firstname ? user.firstname.charAt(0).toUpperCase() : 'U'}
                    </Avatar>
                  ) : (
                    <AccountCircleIcon />
                  )}
                </StyledIconButton>
              </Tooltip>

              {/* Cart Icon */}
              <Tooltip title="Shopping Cart">
                <StyledIconButton aria-label="shopping cart" onClick={() => handleIconClick('/cart')}>
                  <Badge
                    badgeContent={cartItemsCount}
                    sx={{
                      '& .MuiBadge-badge': {
                        bgcolor: COLORS.gold,
                        color: COLORS.textPrimary,
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        minWidth: 18,
                        height: 18,
                        borderRadius: '9px'
                      }
                    }}
                  >
                    <ShoppingBagOutlined />
                  </Badge>
                </StyledIconButton>
              </Tooltip>
            </IconsContainer>

            {isMobile && mobileSearchVisible && (
              <Box
                data-testid="mobile-search-row"
                sx={{
                  gridColumn: '1 / -1',
                  gridRow: 2,
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 0,
                  width: '100%',
                  pb: 1,
                }}
              >
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder={t('header.searchPlaceholder')}
                  inputProps={{ 'aria-label': 'Search products' }}
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 20, backgroundColor: '#f5f5f5' }
                  }}
                  sx={{ width: '100%', minWidth: 0 }}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchSubmit}
                  onBlur={() => setTimeout(() => setMobileSearchVisible(false), 100)}
                />
              </Box>
            )}

          </Toolbar>
        </Container>

        <Box
          ref={containerRef}
          data-testid="desktop-nav-row"
          sx={{
            borderTop: `1px solid ${COLORS.borderHairline}`,
            borderBottom: `1px solid ${COLORS.borderHairline}`,
            minHeight: 46,
            display: { xs: 'none', md: 'flex' },
            justifyContent: 'center',
            alignItems: 'center',
            gap: { md: '16px', lg: '28px' },
            position: 'relative',
          }}
        >
          {visibleItems.map((item) => (
            <Box
              key={`${item.type}-${item.id}`}
              component={Link}
              to={menuItemPath(item)}
              onClick={() => setSearchQuery('')}
              sx={desktopNavItemSx(item)}
            >
              {getTitle(item.title)}
            </Box>
          ))}
          {overflowItems.length > 0 && (
            <Box
              component="button"
              type="button"
              aria-controls={Boolean(moreMenuAnchor) ? 'more-menu-appbar' : undefined}
              aria-haspopup="true"
              aria-expanded={Boolean(moreMenuAnchor) ? 'true' : undefined}
              onClick={openMoreMenu}
              sx={desktopNavItemSx({ title: 'More' })}
            >
              More ▾
            </Box>
          )}
          <Box
            aria-hidden="true"
            sx={{
              display: 'flex',
              gap: { md: '16px', lg: '28px' },
              position: 'absolute',
              visibility: 'hidden',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}
          >
            {navItems.map((item) => (
              <Box
                key={`measure-${item.type}-${item.id}`}
                component="span"
                data-nav-measure-item
                sx={{
                  fontFamily: FONTS.body,
                  fontSize: { md: '0.6875rem', lg: '0.8125rem' },
                  fontWeight: 600,
                  letterSpacing: { md: '1px', lg: '1.5px' },
                  lineHeight: 1.5,
                  textTransform: 'uppercase',
                }}
              >
                {getTitle(item.title)}
              </Box>
            ))}
          </Box>
          <Menu
            id="more-menu-appbar"
            anchorEl={moreMenuAnchor}
            open={Boolean(moreMenuAnchor)}
            onClose={closeMoreMenu}
            keepMounted
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {overflowItems.map((item) => (
              <MenuItem
                key={`more-${item.type}-${item.id}`}
                component={Link}
                to={menuItemPath(item)}
                onClick={() => {
                  setSearchQuery('');
                  closeMoreMenu();
                }}
                sx={{
                  ...drawerNavItemSx(item),
                  py: 1,
                }}
              >
                {getTitle(item.title)}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </StyledAppBar>

      {/* Mobile Drawer */}
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer}>
        <Box sx={{ width: 280, boxSizing: 'border-box', p: '18px 22px' }} role="presentation">
          <List sx={{ p: 0 }}>
            <ListItem sx={{ justifyContent: 'center', p: 0, pb: '18px' }}>
              <LogoLink to="/" onClick={closeDrawerNavigation}>
                <Logo src="/eladorelogosmall2.png" alt="Eladore Ruffles" />
              </LogoLink>
            </ListItem>
            <ListItem sx={{ justifyContent: 'center', p: 0, pb: '12px' }}>
              <LanguageSwitcher
                enabled={i18nLoaded && i18nConfig.switcherEnabled}
                languagesInSwitcher={i18nConfig.languagesInSwitcher}
                variant="drawer"
              />
            </ListItem>
            <Divider />
            <Box
              component="nav"
              aria-label="Mobile"
              sx={{ display: 'flex', flexDirection: 'column', gap: '15px', py: '18px' }}
            >
              {navItems.map((item) => (
                <Box
                  key={`drawer-${item.type}-${item.id}`}
                  component={Link}
                  to={menuItemPath(item)}
                  onClick={closeDrawerNavigation}
                  sx={drawerNavItemSx(item)}
                >
                  {getTitle(item.title)}
                </Box>
              ))}
            </Box>
            <Divider sx={{ mb: 1 }} />
            <ListItem key="drawer-profile-link" disablePadding>
              <ListItemButton onClick={() => handleIconClick(isAuthenticated ? '/dashboard' : '/login')}>
                <ListItemIcon>
                  <AccountCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={isAuthenticated ? t('nav.myProfile') : t('nav.loginRegister')} />
              </ListItemButton>
            </ListItem>
            <ListItem key="drawer-wishlist-link" disablePadding>
              <ListItemButton onClick={() => { navigate('/wishlist'); toggleDrawer(); }}>
                <ListItemIcon>
                  <Badge badgeContent={wishlistCount} color="secondary" sx={{ mr: wishlistCount > 0 ? 1 : 0 }}>
                    <FavoriteBorderIcon fontSize="small" />
                  </Badge>
                </ListItemIcon>
                <ListItemText primary={t('nav.wishlist')} />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Profile Dropdown Menu (Structure remains similar) */}
      <Menu
        id="profile-menu-appbar"
        anchorEl={profileMenuAnchor}
        open={Boolean(profileMenuAnchor)}
        onClose={handleProfileMenuClose}
        keepMounted
        autoFocus
        transitionDuration={300}
        MenuListProps={{ dense: false, 'aria-labelledby': 'profile-button' }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
            width: 350, // Keep width or adjust as needed
            maxHeight: '80vh',
            overflowY: 'auto',
            '& .MuiMenuItem-root': { px: 2, py: 1 }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {[
          // User Info Box
          <Box key="user-info" sx={{ p: 2, borderBottom: '1px solid #eaeaea' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {user?.firstname} {user?.lastname}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>,

          // Regular user options
          <MenuItem key="profile" onClick={() => handleProfileMenuItemClick('/dashboard')}>
            <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={t('nav.myProfile')} />
          </MenuItem>,
          <MenuItem key="orders" onClick={() => handleProfileMenuItemClick('/my-orders')}>
            <ListItemIcon><ReceiptIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={t('nav.myOrders')} />
          </MenuItem>,

          // Admin Panel Menu items
          ...(isAdmin ? [
            <Divider key="admin-divider" />,
            <Box key="admin-header" sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">Admin Panel</Typography>
            </Box>,
            <MenuItem key="/admin" onClick={() => handleProfileMenuItemClick('/admin')}>
              <ListItemIcon><DashboardIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Admin Dashboard" />
            </MenuItem>,
            <Box key="quick-actions-header" sx={{ px: 2, py: 1, mt: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">Quick Actions</Typography>
            </Box>,
            ...adminMenuItems.filter(item => item.path !== '/admin').map((item) => (
              <MenuItem key={item.path} onClick={() => handleProfileMenuItemClick(item.path)} sx={{ py: 0.5 }}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </MenuItem>
            ))
          ] : []),

          // Logout option
          <Divider key="logout-divider" />,
          <Box key="logout-container" sx={{ px: 2, py: 1 }}>
            <MenuItem
              key="logout"
              onClick={handleLogout}
              sx={{
                bgcolor: 'error.main',
                color: 'white',
                borderRadius: 1,
                my: 1,
                '&:hover': { bgcolor: 'error.dark' }
              }}
            >
              <ListItemIcon sx={{ color: 'white' }}><LogoutIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary={t('nav.logout')} primaryTypographyProps={{ fontWeight: 'bold', variant: 'button', textAlign: 'center' }} />
            </MenuItem>
          </Box>
        ]}
      </Menu>
    </>
  );
};

export default UnifiedHeader;
